
import { Component, Prop, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import BasicButton from '@/ui-components/BasicButton.vue';

import { CollectionType } from '@/enums/collection-type';

import { trackClickOutGraphql } from '@/helpers/offer-helper';
import {
	SnowplowClickoutContextGraphql,
	SnowplowContext,
	SnowplowTitleContextGraphql,
} from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { TabType } from '@/interfaces/inner-tabs';
import { OfferPresentationType } from '@/interfaces/titles';

import WatchNowModal from '@/components/modals/WatchNowModal.vue';
import { ModalHelper } from '@/helpers/modal-helper';
import { WatchlistTitleGraphqlFragment } from '@/components/watchlist/graphql/fragments/WatchlistTitle.fragment';
import { FilterCollection, getDefaultFilterCollection } from '@/stores/filter.store';

import { Package } from '@/@types/graphql-types';
import UseOfferImpressionTracking from '@/helpers/composables/useOfferImpressionTracking';
import { isFilterActive } from '@/helpers/filter-helper';
import { TvShowTrackingTitleFragment } from '@/components/watchlist/graphql/fragments/TVShowTrackingTitle.fragment';
import { pluck } from '@/helpers/object-helper';
import { ClickoutUrl } from '@/helpers/clickout-helper';

const Constant = namespace('constant');
const Filter = namespace('filter');
const Language = namespace('language');
const Routing = namespace('routing');
const User = namespace('user');

@Component({
	name: 'WatchNowButtonNew',
	components: { ProviderIcon, BasicButton, UseOfferImpressionTracking },
})
export default class WatchNowButtonNew extends Vue {
	private domainSessionId: string | null = null;
	private collectionType: CollectionType;

	@Prop({ required: true }) title: WatchlistTitleGraphqlFragment['node'] | TvShowTrackingTitleFragment['node'];
	@Prop({ default: () => ({}) }) customFilters?: FilterCollection;
	@Prop({ default: 'default' }) size: 'default' | 'large' | 'slim';
	@Prop() rounded?: boolean;
	@Prop({ default: 'secondary' }) color: 'secondary' | 'primary';
	@Prop({ default: false }) uniqueProviders: boolean;
	@Prop({ default: false }) inQuickActions?: boolean;
	@Prop({ default: false }) isNextEpisodeButton: boolean;
	@Prop({ default: () => [] }) additionalContexts: SnowplowContext[];

	@Constant.State providers: Package[];

	@Filter.Getter filters: (collectionType: CollectionType) => FilterCollection;

	@Language.Getter language: string;
	@Language.Getter country: string;

	@Routing.Getter activeCollectionType: CollectionType;

	@User.Action setBuyboxPresentationType: (payload: {
		presentationType: OfferPresentationType | null;
		additionalContexts?: SnowplowContext[];
	}) => void;

	get providersById() {
		return pluck(this.providers, 'packageId');
	}

	get providersByShortName() {
		return pluck(this.providers, 'shortName');
	}

	get buttonText() {
		if (this.noOffer) {
			return this.$t('WEBAPP_CURRENTLY_NO_OFFERS');
		}

		const message = this.isSeenTab ? this.$t('WEBAPP_WATCH_AGAIN') : this.$t('WEBAPP_SEENLIST_WATCHNOW');

		const offersTranslation = this.moreThanOneOffer
			? this.$t('WEBAPP_X_OFFERS_AVAILABLE', { offerCount: this.offerCount })
			: this.$t('WEBAPP_1_OFFER_AVAILABLE', { offerCount: this.offerCount });

		return this.isWatchnowOfferAvailable ? message : offersTranslation;
	}

	get currentFilters() {
		return {
			...(this.filters(this.collectionType) ?? getDefaultFilterCollection()),
			...this.customFilters,
		};
	}

	get isSeenTab() {
		return isFilterActive('inner_tab', TabType.SEENLIST, this.currentFilters);
	}

	get providerIcon() {
		return this.watchNowOffer?.package.packageId || null;
	}

	get isWatchnowOfferAvailable() {
		return !!this.watchNowOffer;
	}

	get moreThanOneOffer() {
		return this.offerCount > 1;
	}

	get noOffer() {
		return this.offerCount === 0;
	}

	//TODO [GraphQL] Remove those getters after component refactoring (divide title prop into more specific props)
	get offerCount() {
		if (this.uniqueProviders)
			// @ts-ignore
			return [...new Set(this.title.offers.map(offer => offer.package?.packageId).filter(Boolean))].length;
		return this.isNextEpisodeButton && 'watchNextEpisode' in this.title
			? this.title.watchNextEpisode?.offerCount || 0
			: this.title.offerCount;
	}

	get watchNowOffer() {
		return this.watchNextEpisode ? this.watchNextEpisode.watchNowOffer : this.title.watchNowOffer;
	}

	get watchNextEpisode() {
		return 'watchNextEpisode' in this.title && this.isNextEpisodeButton && this.title.watchNextEpisode;
	}

	get seasonNumber() {
		return this.watchNextEpisode ? this.watchNextEpisode.content.seasonNumber : null;
	}

	get episodeNumber() {
		return this.watchNextEpisode ? this.watchNextEpisode.content.episodeNumber : null;
	}

	get offerContexts() {
		const { objectId, objectType } = this.title;

		return [
			...this.additionalContexts,
			TrackingHelper.getTitleContextGraphql(
				objectId,
				objectType,
				this.seasonNumber || undefined,
				this.episodeNumber || undefined
			),
		];
	}

	beforeMount() {
		// collectionType will not change anymore
		// we don't rely on activeCollectionType or currentFilters because it would recalculate when the store changes
		this.collectionType = this.activeCollectionType;
	}

	watchNow() {
		const { objectId, objectType } = this.title;

		const contexts = (this.additionalContexts || []).concat([
			new SnowplowTitleContextGraphql(objectId, objectType, this.seasonNumber, this.episodeNumber),
		]);

		if (!this.isWatchnowOfferAvailable) {
			ModalHelper.openModal(WatchNowModal, {
				title: this.title,
				isNextEpisodeButton: this.isNextEpisodeButton,
			}).then(() => {
				this.setBuyboxPresentationType({ presentationType: null });
			});
		}

		// directly clickout when only 1 provider is selected
		const offer = this.watchNowOffer;

		const url = new ClickoutUrl(offer)
			.set('uct_country', this.country)
			.set('sid', this.domainSessionId)
			.set('uct_tryout', 1)
			.toString();

		if (offer) {
			if (offer?.standardWebURL) {
				window.open(url ? url : offer?.standardWebURL, '_blank');
			}
			const clickoutContext = SnowplowClickoutContextGraphql.fromProviderOffer(offer);
			contexts.push(clickoutContext);

			trackClickOutGraphql({
				offer,
				objectType,
				objectId,
				seasonNumber: this.seasonNumber,
				episodeNumber: this.episodeNumber,
				additionalContexts: this.additionalContexts,
			});

			TrackingHelper.trackEvent(
				'userinteraction',
				{
					action: 'watchnow_clicked',
					property: this.inQuickActions + '',
				},
				contexts
			);
		}
	}
}
